import { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';

import { approvePurchaseOrder } from '#/api/srm/purchaseOrder/approve';
import { cancelDraftPurchaseOrder } from '#/api/srm/purchaseOrder/cancelDraft';
import { closePurchaseOrder } from '#/api/srm/purchaseOrder/close';
import { fetchPurchaseOrderDetail } from '#/api/srm/purchaseOrder/fetchDetail';
import { fetchPurchaseOrderStockReceiptList } from '#/api/srm/purchaseOrder/fetchStockReceiptList';
import { rejectPurchaseOrder } from '#/api/srm/purchaseOrder/reject';
import { submitPurchaseOrder } from '#/api/srm/purchaseOrder/submit';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import SideMsg from '#/modules/commonComponents/SideMsg';
import DocumentRejectReasonDialog from '#/modules/imsMgmt/receipts/shared/DocumentRejectReasonDialog';
import { useCan } from '#/modules/infra/auth/useCan';
import {
  purchaseOrderSideMsgDetailBlock,
  purchaseOrderSideMsgStatusBlock,
} from '#/modules/srmMgmt/purchaseOrder/data/purchaseOrderSideMsgConfig';
import {
  getPurchaseOrderApiErrorMessage,
  isPurchaseOrderVersionConflict,
} from '#/modules/srmMgmt/purchaseOrder/helper/apiError';
import {
  canApprovePurchaseOrder,
  canCancelDraftPurchaseOrder,
  canClosePurchaseOrder,
  canEditPurchaseOrderDraft,
  canRejectPurchaseOrder,
  canShowClosePurchaseOrderAction,
  canSubmitPurchaseOrder,
} from '#/modules/srmMgmt/purchaseOrder/helper/purchaseOrderStatus';
import { toast } from '#/utils/toast';
import {
  purchaseOrderDetailQueryKey,
  purchaseOrderListQueryKeyPrefix,
  purchaseOrderStockReceiptListQueryKey,
} from '#/utils/queryKeys';

const PurchaseOrderSideMsg = ({ displayData, setSelectedData }) => {
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const canCreate = useCan(PERM.PURCHASE_ORDER_CREATE);
  const canUpdate = useCan(PERM.PURCHASE_ORDER_UPDATE);
  const canReview = useCan(PERM.PURCHASE_ORDER_REVIEW);

  const id = displayData?.id;
  const status = displayData?.status;

  const canOpenDetail = Boolean(id);
  const canEdit = canUpdate && canEditPurchaseOrderDraft(status);
  const canSubmit = canCreate && canSubmitPurchaseOrder(status);
  const canApprove = canReview && canApprovePurchaseOrder(status);
  const canReject = canReview && canRejectPurchaseOrder(status);
  const canCancelDraft = canCreate && canCancelDraftPurchaseOrder(status);
  const closeEligibleByStatus = canReview && canClosePurchaseOrder(status);

  const stockReceiptListQuery = useQuery({
    queryKey: purchaseOrderStockReceiptListQueryKey(id, {}),
    queryFn: () => fetchPurchaseOrderStockReceiptList(id, {}),
    enabled: Boolean(id) && closeEligibleByStatus,
  });

  const stockReceiptCount = stockReceiptListQuery.data?.data?.length
    ?? displayData?.stockReceiptCount
    ?? 0;
  const canClose = canReview && (stockReceiptListQuery.isSuccess
    ? canShowClosePurchaseOrderAction(status, stockReceiptCount)
    : (
      canClosePurchaseOrder(status)
      && displayData?.stockReceiptCount != null
      && canShowClosePurchaseOrderAction(status, displayData.stockReceiptCount)
    ));

  const hasWorkflowActions = canSubmit || canApprove || canReject || canClose || canCancelDraft;
  const hasActions = canOpenDetail || canEdit || hasWorkflowActions;

  async function refreshSelected() {
    if (!id) {
      return;
    }
    try {
      const detail = await fetchPurchaseOrderDetail(id);
      setSelectedData(detail?.data ?? null);
    } catch {
      setSelectedData(null);
    }
  }

  async function invalidateListAndRefreshSelected(nextDetail) {
    await queryClient.invalidateQueries({
      queryKey: purchaseOrderListQueryKeyPrefix(),
    });
    if (id) {
      await queryClient.invalidateQueries({
        queryKey: purchaseOrderDetailQueryKey(id),
      });
    }
    if (nextDetail) {
      setSelectedData(nextDetail);
      return;
    }
    await refreshSelected();
  }

  const commandMutation = useMutation({
    mutationFn: async ({ action, payload }) => {
      if (action === 'submit') {
        return submitPurchaseOrder(id, payload);
      }
      if (action === 'approve') {
        return approvePurchaseOrder(id, payload);
      }
      if (action === 'reject') {
        return rejectPurchaseOrder(id, payload);
      }
      if (action === 'close') {
        return closePurchaseOrder(id, payload);
      }
      if (action === 'cancelDraft') {
        return cancelDraftPurchaseOrder(id, payload);
      }
      throw new Error(`Unknown action: ${action}`);
    },
    onSuccess: async (result, variables) => {
      toast.success(result?.message || '操作成功');
      if (variables.action === 'reject') {
        setRejectDialogOpen(false);
      }
      await invalidateListAndRefreshSelected(result?.data ?? null);
    },
    onError: (error) => {
      toast.error(getPurchaseOrderApiErrorMessage(error));
      if (isPurchaseOrderVersionConflict(error)) {
        refreshSelected();
      }
    },
  });

  const isPending = commandMutation.isPending;

  function pickUpdatedAt() {
    return displayData?.record?.updatedAt ?? displayData?.updatedAt;
  }

  async function handleSubmit() {
    const ok = await confirm({
      title: '提交物料採購單',
      message: '提交後進入待核准，確定提交？',
      confirmText: '提交',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'submit', payload: { updatedAt: pickUpdatedAt() } });
  }

  async function handleApprove() {
    const ok = await confirm({
      title: '核准物料採購單',
      message: '核准後可進行入庫相關流程，確定核准？',
      confirmText: '核准',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'approve', payload: { updatedAt: pickUpdatedAt() } });
  }

  function handleRejectConfirm(rejectReason) {
    commandMutation.mutate({
      action: 'reject',
      payload: { updatedAt: pickUpdatedAt(), rejectReason },
    });
  }

  async function handleClose() {
    const ok = await confirm({
      title: '結案物料採購單',
      message: '結案後不可再入庫，確定結案？',
      confirmText: '結案',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'close', payload: { updatedAt: pickUpdatedAt() } });
  }

  async function handleCancelDraft() {
    const ok = await confirm({
      title: '取消草稿',
      message: '將軟取消為「已取消」並保留單據，確定取消？',
      confirmText: '取消草稿',
      destructive: true,
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'cancelDraft', payload: { updatedAt: pickUpdatedAt() } });
  }

  const blocks = [
    purchaseOrderSideMsgStatusBlock,
    {
      condition: () => hasActions,
      fields: [
        {
          key: 'actions',
          label: '操作',
          render: () => (
            <div className="side_msg-row">
              <h3>操作</h3>
              <div className="flex flex-wrap items-center gap-2">
                {canOpenDetail ? (
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.purchaseOrderDetail(id)}>詳情</Link>
                  </Button>
                ) : null}
                {canEdit ? (
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.purchaseOrderEdit(id)}>編輯</Link>
                  </Button>
                ) : null}
                {canSubmit ? (
                  <Button type="button" disabled={isPending} onClick={handleSubmit}>
                    提交
                  </Button>
                ) : null}
                {canApprove ? (
                  <Button type="button" disabled={isPending} onClick={handleApprove}>
                    核准
                  </Button>
                ) : null}
                {canReject ? (
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={isPending}
                    onClick={() => setRejectDialogOpen(true)}
                  >
                    退回
                  </Button>
                ) : null}
                {canClose ? (
                  <Button type="button" disabled={isPending} onClick={handleClose}>
                    結案
                  </Button>
                ) : null}
                {canCancelDraft ? (
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={isPending}
                    onClick={handleCancelDraft}
                  >
                    取消草稿
                  </Button>
                ) : null}
              </div>
            </div>
          ),
        },
      ],
    },
    purchaseOrderSideMsgDetailBlock,
  ];

  return (
    <>
      {ConfirmDialogHost}
      <DocumentRejectReasonDialog
        open={rejectDialogOpen}
        onOpenChange={setRejectDialogOpen}
        title="退回物料採購單"
        description="退回後將恢復為草稿，確定退回？"
        confirmText="退回"
        isPending={isPending}
        onConfirm={handleRejectConfirm}
      />
      <SideMsg
        title="物料採購單"
        titleValue="purchaseOrderNo"
        displayData={displayData}
        blocks={blocks}
      />
    </>
  );
};

PurchaseOrderSideMsg.propTypes = {
  displayData: PropTypes.object,
  setSelectedData: PropTypes.func.isRequired,
};

export default PurchaseOrderSideMsg;
